import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { MAX_SCORE, MIN_SCORE, SCALE, scalePoint, scoreLabel, type Entry } from '@fatigue/shared';

interface Props {
  readonly entries: readonly Entry[];
  readonly selectedEntryId: string | null;
  readonly onSelect: (entryId: string | null) => void;
}

const W = 1000;
const H = 340;
const PAD = { top: 20, right: 24, bottom: 34, left: 190 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

/** A gap wider than this breaks the line rather than being drawn across. */
const GAP_MS = 24 * 60 * 60 * 1000;

const ROLLING_WINDOW = 5;

interface Point {
  readonly entry: Entry;
  readonly x: number;
  readonly y: number;
  readonly t: number;
}

/**
 * Hand-rolled SVG, not a charting library. The interaction requirements are
 * specific enough that a library fights you:
 *
 *   - Y axis from 1 Completely Drained at the bottom to 6 Fully Energised
 *     at the TOP, each level labelled with a swatch, its number and its name.
 *     Colour is never the only channel.
 *   - Raw line at low opacity, plus a dashed 5-point rolling mean.
 *   - Entries WITH a comment are hollow rings; entries WITHOUT are filled
 *     dots. Shape, not just colour — this is how a clinician finds the
 *     comments at a glance.
 *   - A transparent 15px hit circle on every point, because a 4px dot is not a
 *     realistic target.
 *   - Gaps longer than a day break the line. A gap is data: a patient too
 *     fatigued to open the app is telling you something, and interpolating
 *     across it erases exactly that.
 */
export function FatigueChart({ entries, selectedEntryId, onSelect }: Props): JSX.Element {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const { points, segments, meanSegments } = useMemo(() => build(entries), [entries]);

  if (points.length === 0) {
    return <p className="empty">No entries in this period.</p>;
  }

  const active = points.find((p) => p.entry.entryId === (hoveredId ?? selectedEntryId)) ?? null;

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="chart__svg"
        role="img"
        aria-label={`Fatigue scores, ${points.length} entries. Use the comment list to read individual entries.`}
      >
        {/* Gridlines and Y labels */}
        {SCALE.map((level) => {
          const y = yFor(level.score);
          return (
            <g key={level.score}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} className="chart__grid" />
              <rect x={PAD.left - 22} y={y - 6} width={12} height={12} rx={3} fill={level.colour} />
              <text x={PAD.left - 30} y={y + 4} className="chart__yname" textAnchor="end">
                {scoreLabel(level.score)}
              </text>
            </g>
          );
        })}

        {/* X axis ticks: first and last day in view */}
        <text x={PAD.left} y={H - 10} className="chart__xlabel">
          {formatDay(points[0]!.t)}
        </text>
        <text x={W - PAD.right} y={H - 10} className="chart__xlabel" textAnchor="end">
          {formatDay(points[points.length - 1]!.t)}
        </text>

        {/* Raw line, low opacity — the shape, not the focus */}
        {segments.map((segment, i) => (
          <polyline key={`raw-${i}`} points={segment} className="chart__raw" />
        ))}

        {/* Dashed 5-point rolling mean */}
        {meanSegments.map((segment, i) => (
          <polyline key={`mean-${i}`} points={segment} className="chart__mean" />
        ))}

        {/* Points. Rings carry comments; dots do not. */}
        {points.map((point) => {
          // A 4px dot and a 2.5px ring are thin marks on a white plot, so both
          // take the darkened step. The fill ramp stays for areas.
          const colour = scalePoint(point.entry.score).ink;
          const selected = point.entry.entryId === selectedEntryId;
          const hasComment = point.entry.comment !== '';

          return (
            <g key={point.entry.entryId}>
              {selected && <circle cx={point.x} cy={point.y} r={11} className="chart__halo" />}
              {hasComment ? (
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={5.5}
                  fill="var(--panel)"
                  stroke={colour}
                  strokeWidth={2.5}
                />
              ) : (
                <circle cx={point.x} cy={point.y} r={4} fill={colour} />
              )}
              {/* Transparent hit target — the reason a library fights you. */}
              <circle
                cx={point.x}
                cy={point.y}
                r={15}
                fill="transparent"
                className="chart__hit"
                onMouseEnter={() => setHoveredId(point.entry.entryId)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => onSelect(point.entry.entryId)}
              />
            </g>
          );
        })}
      </svg>

      {active !== null && (
        <div
          className="chart__tip"
          style={{
            left: `${(active.x / W) * 100}%`,
            top: `${(active.y / H) * 100}%`,
          }}
        >
          <strong>{scoreLabel(active.entry.score)}</strong>
          <span>{formatFull(active.t)}</span>
          {active.entry.comment !== '' && <em>{active.entry.comment}</em>}
        </div>
      )}
    </div>
  );
}

const yFor = (score: number): number =>
  PAD.top + ((MAX_SCORE - score) / (MAX_SCORE - MIN_SCORE)) * PLOT_H;

function build(entries: readonly Entry[]): {
  points: Point[];
  segments: string[];
  meanSegments: string[];
} {
  if (entries.length === 0) return { points: [], segments: [], meanSegments: [] };

  const sorted = [...entries].sort(
    (a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime(),
  );

  const times = sorted.map((e) => new Date(e.capturedAt).getTime());
  const min = times[0]!;
  const max = times[times.length - 1]!;
  // A single entry, or several within one instant, would divide by zero.
  const span = max - min || 1;

  const points: Point[] = sorted.map((entry, i) => {
    const t = times[i]!;
    return {
      entry,
      t,
      x: PAD.left + ((t - min) / span) * PLOT_W,
      y: yFor(entry.score),
    };
  });

  // Rolling mean over the trailing window, in entry order.
  const means = points.map((point, i) => {
    const from = Math.max(0, i - ROLLING_WINDOW + 1);
    const window = points.slice(from, i + 1);
    const mean = window.reduce((sum, p) => sum + p.entry.score, 0) / window.length;
    return { x: point.x, y: yFor(mean), t: point.t };
  });

  return {
    points,
    segments: splitOnGaps(points.map((p) => ({ x: p.x, y: p.y, t: p.t }))),
    meanSegments: splitOnGaps(means),
  };
}

/**
 * Breaks a series wherever consecutive entries are more than a day apart, so
 * the gap is visible rather than smoothed over. Single-point segments are
 * dropped — a polyline of one point draws nothing, and the dot already marks it.
 */
function splitOnGaps(series: readonly { x: number; y: number; t: number }[]): string[] {
  const segments: string[] = [];
  let current: string[] = [];
  let previousT: number | null = null;

  for (const item of series) {
    if (previousT !== null && item.t - previousT > GAP_MS) {
      if (current.length > 1) segments.push(current.join(' '));
      current = [];
    }
    current.push(`${item.x.toFixed(1)},${item.y.toFixed(1)}`);
    previousT = item.t;
  }

  if (current.length > 1) segments.push(current.join(' '));
  return segments;
}

const formatDay = (t: number): string =>
  new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const formatFull = (t: number): string =>
  `${new Date(t).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })} · ${new Date(t).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
